import {
  GridItem,
  LayoutGrid,
  type RadialGradientBGProps,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';
import { useCallback, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import FeaturesCarousel from './FeaturesCarousel';
import FeaturesEyebrow from './FeaturesEyebrow';
import FeaturesTabs from './FeaturesTabs';
import MobileFeature from './MobileFeature';
import type {
  LandingPageFeature,
  LandingPageFeaturesTextColor,
  LandingPageFeaturesTheme,
} from './types';

export interface LandingPageFeaturesProps
  extends ComponentPropsWithoutRef<'section'> {
  readonly eyebrow: MessageKey;
  readonly features: LandingPageFeature[];
  readonly imageGradientBg?: RadialGradientBGProps;
  readonly textColor?: LandingPageFeaturesTextColor;
  readonly theme?: LandingPageFeaturesTheme;
}

const LandingPageFeatures = ({
  eyebrow,
  features,
  imageGradientBg,
  textColor = 'sandstone-500',
  theme = 'dark',
  ...props
}: LandingPageFeaturesProps) => {
  const [activeFeatureIndex, setActiveFeatureIndex] = useState<number>(0);
  const [carouselCountKey, setCarouselCountKey] = useState(0);

  const handlePreviousClick = useCallback(() => {
    setActiveFeatureIndex((currentIndex) => Math.max(currentIndex - 1, 0));
    setCarouselCountKey((prev) => prev + 1);
  }, [setActiveFeatureIndex]);

  const handleNextClick = useCallback(() => {
    setActiveFeatureIndex((currentIndex) =>
      Math.min(features.length - 1, currentIndex + 1),
    );
    setCarouselCountKey((prev) => prev + 1);
  }, [features, setActiveFeatureIndex]);

  const activeColor = {
    'text-brown-600': textColor === 'brown-600',
    'text-olive-800': textColor === 'olive-800',
    'text-sandstone-200': textColor === 'sandstone-200',
    'text-sandstone-500': textColor === 'sandstone-500',
    'text-mustard-700': textColor === 'mustard-700',
  };

  return (
    <section
      className="pt-16 xl:py-16"
      data-cy="landing-page-features"
      {...props}
    >
      <LayoutGrid
        className={cx('lg:hidden', activeColor)}
        data-cy="landing-page-features-mobile"
      >
        <GridItem
          colEnd={{ sm: 'main', md: 20 }}
          colStart={{ sm: 'main', md: 6 }}
        >
          <FeaturesEyebrow label={eyebrow} />
        </GridItem>
        {features.map((feature, i) => (
          <MobileFeature
            key={feature.id}
            imageGradientBg={imageGradientBg}
            index={i}
            {...feature}
          />
        ))}
      </LayoutGrid>

      <LayoutGrid
        className={cx('hidden lg:grid', activeColor)}
        data-cy="landing-page-features-desktop"
      >
        <GridItem
          colEnd={{ sm: 'main', lg: 13, xl: 10 }}
          colStart={{ sm: 'main', lg: 'main' }}
        >
          <div className="flex h-full flex-col justify-end pb-16">
            <FeaturesEyebrow label={eyebrow} />
            <FeaturesTabs
              activeFeatureIndex={activeFeatureIndex}
              features={features}
              onTabClick={setActiveFeatureIndex}
            />
          </div>
        </GridItem>
        <GridItem
          className="lg:pl-4 xl:pl-0"
          colEnd={{ sm: 'main', lg: 'full', xxl: 'main' }}
          colStart={{ sm: 'main', lg: 13 }}
        >
          <FeaturesCarousel
            activeFeatureIndex={activeFeatureIndex}
            features={features}
            imageGradientBg={imageGradientBg}
            carouselCountKey={carouselCountKey}
            onNextClick={handleNextClick}
            onPreviousClick={handlePreviousClick}
            theme={theme}
          />
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default LandingPageFeatures;
